import { useRef, useEffect, useState } from "react";
import {
  problemContent,
  solutionContent,
  contactContent,
  blogContent,
  footerContent,
  siteConfig,
} from "../../data/mock";
import {
  ArrowRight,
  Calendar,
  Terminal,
  Mail,
  ExternalLink,
} from "lucide-react";

const useInView = (threshold = 0.15) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);

  return [ref, inView];
};

/* ==================== PROBLEM SECTION ==================== */
export const ProblemSection = () => {
  const [ref, inView] = useInView();

  return (
    <section
      id="problems"
      ref={ref}
      className="relative px-6 lg:px-8 py-24 lg:py-32"
    >
      <div className="absolute top-0 left-6 right-6 lg:left-8 lg:right-8 h-px bg-black/[0.06]" />

      <div className="max-w-7xl mx-auto w-full">
        <div
          className={`transition-all duration-1000 ${inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
        >
          <h2 className="font-display text-3xl sm:text-4xl lg:text-[2.75rem] font-semibold text-[#1a1a1a] max-w-4xl leading-tight mb-5">
            {problemContent.heading}
          </h2>
          <p className="text-base lg:text-lg text-[#1a1a1a]/45 max-w-3xl leading-relaxed mb-16">
            {problemContent.description}
          </p>
        </div>

        <div
          className={`grid md:grid-cols-2 xl:grid-cols-3 gap-px bg-black/[0.06] border border-black/[0.06] mb-14 transition-all duration-1000 delay-200 ${
            inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          {problemContent.points.map((point, i) => (
            <div
              key={i}
              className="bg-white p-8 lg:p-10 group hover:bg-emerald-700/[0.02] transition-colors duration-500"
            >
              <div className="font-mono text-2xl lg:text-3xl font-bold text-emerald-700 mb-5">
                {point.stat}
              </div>
              <div className="font-display text-base font-medium text-[#1a1a1a]/80 mb-2">
                {point.title}
              </div>
              <div className="text-sm text-[#1a1a1a]/40 leading-relaxed">
                {point.description}
              </div>
            </div>
          ))}
        </div>

        <div
          className={`transition-all duration-1000 delay-400 ${
            inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <div className="grid sm:grid-cols-2 gap-4">
            {problemContent.consequences.map((item, i) => (
              <div
                key={i}
                className="flex items-start gap-3 p-4 border border-black/[0.06] hover:border-black/[0.12] transition-colors duration-300"
              >
                <span className="font-mono text-xs text-[#1a1a1a]/15 shrink-0 mt-0.5">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="text-sm text-[#1a1a1a]/55">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ==================== SOLUTION SECTION ==================== */
export const SolutionSection = () => {
  const [ref, inView] = useInView();

  return (
    <section
      id="solution"
      ref={ref}
      className="relative min-h-screen flex items-center px-6 lg:px-8 py-24 lg:py-32"
    >
      <div className="absolute top-0 left-6 right-6 lg:left-8 lg:right-8 h-px bg-black/[0.06]" />

      <div className="max-w-7xl mx-auto w-full">
        <div
          className={`transition-all duration-1000 ${inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
        >
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#1a1a1a] max-w-3xl leading-tight mb-6">
            {solutionContent.heading}
          </h2>

          <p className="text-base lg:text-lg text-[#1a1a1a]/45 max-w-2xl leading-relaxed mb-16">
            {solutionContent.description}
          </p>
        </div>

        {/* Capabilities */}
        <div className="space-y-0">
          {solutionContent.capabilities.map((cap, i) => (
            <div
              key={i}
              className={`grid lg:grid-cols-[1fr_1fr] gap-6 lg:gap-12 border-t border-black/[0.06] py-10 lg:py-14 transition-all duration-1000 ${
                inView
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-8"
              }`}
              style={{ transitionDelay: `${(i + 1) * 200}ms` }}
            >
              <div>
                <div className="font-mono text-xs text-[#1a1a1a]/15 mb-3">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <h3 className="font-display text-xl lg:text-2xl font-semibold text-[#1a1a1a]/85 mb-3">
                  {cap.title}
                </h3>
                <p className="text-sm text-[#1a1a1a]/45 leading-relaxed">
                  {cap.description}
                </p>
              </div>
              {/* Terminal blocks light mode */}
              <div className="border border-black/[0.08] bg-white p-4 font-mono text-sm self-center shadow-lg shadow-black/[0.03]">
                <div className="flex items-center gap-2 text-[#1a1a1a]/20 mb-2">
                  <div className="flex gap-1">
                    <div className="w-2 h-2 rounded-full bg-black/[0.08]" />
                    <div className="w-2 h-2 rounded-full bg-black/[0.08]" />
                    <div className="w-2 h-2 rounded-full bg-black/[0.08]" />
                  </div>
                </div>
                <div className="flex gap-2">
                  <span className="text-[#1a1a1a]/30">$</span>
                  <span className="text-emerald-700/70">{cap.terminal}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ==================== BLOG SECTION ==================== */
export const BlogSection = () => {
  const [ref, inView] = useInView();

  return (
    <section
      id="blog"
      ref={ref}
      className="relative px-6 lg:px-8 py-24 lg:py-32"
    >
      <div className="absolute top-0 left-6 right-6 lg:left-8 lg:right-8 h-px bg-black/[0.06]" />

      <div className="max-w-7xl mx-auto w-full">
        <div
          className={`grid lg:grid-cols-[0.8fr_1.2fr] gap-10 lg:gap-16 transition-all duration-1000 ${
            inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#1a1a1a] leading-tight mb-6">
              {blogContent.heading}
            </h2>
            <p className="text-base lg:text-lg text-[#1a1a1a]/45 leading-relaxed">
              {blogContent.description}
            </p>
          </div>

          <div className="grid gap-px bg-black/[0.06] border border-black/[0.06]">
            {blogContent.posts.map((post, i) => (
              <a
                key={post.title}
                href={post.href}
                className="group bg-white p-7 lg:p-8 hover:bg-emerald-700/[0.02] transition-colors duration-500"
              >
                <div className="font-mono text-xs text-[#1a1a1a]/15 mb-4">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <h3 className="font-display text-xl lg:text-2xl font-semibold text-[#1a1a1a]/85 mb-3 group-hover:text-emerald-700 transition-colors duration-300">
                  {post.title}
                </h3>
                <p className="text-sm text-[#1a1a1a]/45 leading-relaxed">
                  {post.description}
                </p>
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ==================== CONTACT SECTION ==================== */
export const ContactSection = () => {
  const [ref, inView] = useInView();

  return (
    <section
      id="contact"
      ref={ref}
      className="relative min-h-[80vh] flex items-center px-6 lg:px-8 py-24 lg:py-32"
    >
      <div className="absolute top-0 left-6 right-6 lg:left-8 lg:right-8 h-px bg-black/[0.06]" />

      <div className="max-w-4xl mx-auto w-full">
        <div
          className={`transition-all duration-1000 ${inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
        >
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#1a1a1a] leading-tight mb-6 text-center">
            {contactContent.heading}
          </h2>

          <p className="text-base lg:text-lg text-[#1a1a1a]/45 max-w-2xl mx-auto leading-relaxed mb-14 text-center">
            {contactContent.description}
          </p>

          {/* Primary CTA */}
          <div className="flex justify-center mb-14">
            <a
              href={siteConfig.demoUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="group font-mono text-sm px-8 py-3.5 bg-emerald-700 text-white hover:bg-emerald-800 transition-colors duration-300 flex items-center gap-3"
            >
              <Calendar className="w-4 h-4" />
              Book a demo
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
            </a>
          </div>

          {/* Contact Cards */}
          <div
            className={`grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-black/[0.06] border border-black/[0.06] transition-all duration-1000 delay-300 ${
              inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
          >
            <a
              href={`mailto:${contactContent.email}`}
              className="bg-white p-6 group hover:bg-emerald-700/[0.02] transition-colors duration-500 flex flex-col items-center text-center"
            >
              <Mail className="w-5 h-5 text-[#1a1a1a]/20 group-hover:text-emerald-700/60 transition-colors duration-500 mb-3" />
              <div className="font-mono text-sm text-[#1a1a1a]/60 group-hover:text-[#1a1a1a]/80 transition-colors duration-300 break-all">
                {contactContent.email}
              </div>
            </a>

            {contactContent.team.map((member, i) => (
              <a
                key={i}
                href={member.url}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white p-6 group hover:bg-emerald-700/[0.02] transition-colors duration-500 flex flex-col items-center text-center"
              >
                <ExternalLink className="w-5 h-5 text-[#1a1a1a]/20 group-hover:text-emerald-700/60 transition-colors duration-500 mb-3" />
                <div className="font-mono text-sm text-[#1a1a1a]/60 group-hover:text-[#1a1a1a]/80 transition-colors duration-300">
                  {member.name}
                </div>
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ==================== FOOTER ==================== */
export const Footer = () => (
  <footer className="px-6 lg:px-8 py-12 border-t border-black/[0.06]">
    <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
      <div className="flex items-center gap-2">
        <div className="w-6 h-6 border border-emerald-700/25 flex items-center justify-center">
          <Terminal className="w-3 h-3 text-emerald-700/50" />
        </div>
        <span className="font-mono text-sm text-[#1a1a1a]/35">
          {siteConfig.name}
          <span className="text-emerald-700/50">.ai</span>
        </span>
      </div>

      <a
        href={`mailto:${siteConfig.email}`}
        className="font-mono text-xs text-[#1a1a1a]/30 hover:text-[#1a1a1a]/60 transition-colors duration-300"
      >
        {siteConfig.email}
      </a>

      <span className="font-mono text-xs text-[#1a1a1a]/20">
        {footerContent.copyright}
      </span>
    </div>
  </footer>
);
